<template>
  <div class="grow">
    <ContentEditMediaPreview
      v-if="mediaUrl && mediaType"
      :url="mediaUrl"
      :media-type="mediaType"
      :class="small ? 'px-6 @sm:px-10' : ''"
      class="pb-6"
      @accept="handleAttachmentReady"
      @cancel="resetMedia"
    />
    <template v-else>
      <MAInputPrefix
        v-model="inputLink"
        label="Media URL"
        prefix="https://"
        :rules="rules"
        :disabled="isPending"
      />
      <div class="flex justify-end">
        <AppButton
          label="Load"
          variant="secondary"
          :loading="isPending || isProcessing"
          :disabled="isPending"
          @click="loadMedia"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import MAInputPrefix from "@/components/primevueComponents/MAInputPrefix.vue";
import useVuelidate from "@vuelidate/core";
import { required, url } from "@vuelidate/validators";
import ContentEditMediaPreview from "./ContentEditMediaPreview.vue";
import { useI18n } from "vue3-i18n";
import { useToast } from "vue-toast-notification";

defineProps<{
  small?: boolean;
}>();
const emit = defineEmits<{
  imageSelected: [string];
  videoSelected: [string];
}>();

const isProcessing = ref(false);
const rules = { required, url };
const inputLink = ref("");
const mediaUrl = ref("");
const mediaType = ref<"image" | "video" | "">("");
const fileId = ref("");
const { getImage, isPending } = useRemoteImageDownload();
const $v = useVuelidate();
const { t } = useI18n();
const $toast = useToast(defaultToastOptions);
const { data } = useSingleFileRequest(fileId, true);

function loadMedia() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (isPending.value) return;
  isProcessing.value = true;
  getImage(inputLink.value, {
    onSuccess: response => {
      if (!response) {
        $toast.error(t("errors.tryAgain"));
        isProcessing.value = false;
        return;
      }
      fileId.value = response.fileId;
      mediaType.value = response.mediaType;
    },
    onError: () => {
      $toast.error(t("errors.tryAgain"));
      isProcessing.value = false;
    },
  });
}

function handleAttachmentReady() {
  if (!mediaUrl.value || !mediaType.value) return;

  if (mediaType.value === "image") {
    emit("imageSelected", mediaUrl.value);
  } else if (mediaType.value === "video") {
    emit("videoSelected", mediaUrl.value);
  }

  resetMedia();
}

function resetMedia() {
  mediaUrl.value = "";
  mediaType.value = "";
  inputLink.value = "";
  fileId.value = "";
}

watchEffect(() => {
  if (data.value?.path) {
    isProcessing.value = false;
    mediaUrl.value = data.value.path;
  }
});
</script>
